import * as pdfjsLib from 'pdfjs-dist'

pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs'

const PLAYER_DATA = /(\d{2}\/\d{2}\/\d{4})\s+(\d{5,8})\s+([A-Za-zÀ-ÿ'\s]+)/
const MANAGER_DATA = /^(\d{5,8})\s+(.+)$/
const SKIP_PATTERNS = [
  /^=\s*ag\s*tosú/i,
  /^=\s*neamhchláraithe/i,
  /^\(Cathaoirleach\)/i,
  /^\(Rúnaí\)/i,
  /^\(Foireann\)/i,
  /^\(Freasúra\)/i,
  /^\(Comórtas\)/i,
  /^\(Dáta\)/i,
  /^\(Sínithe/i,
  /^Cruthaithe i bhFoireann/i,
  /^\/\s*\//,
  /^\d{1,2}\/\d{1,2}$/,  
  /^GAA$/i,
  /^Imreoirí$/,
]

function shouldSkip(line) {
  return SKIP_PATTERNS.some(p => p.test(line.trim()))
}

function parseDOB(str) {
  const [d, m, y] = str.split('/')
  return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`
}

function groupIntoRows(items, threshold = 4) {
  if (!items.length) return []
  const sorted = [...items].sort((a, b) => {
    if (a.page !== b.page) return a.page - b.page
    if (Math.abs(a.y - b.y) > threshold) return a.y - b.y
    return a.x - b.x
  })
  const rows = []
  let currentRow = [sorted[0]]
  for (let i = 1; i < sorted.length; i++) {
    const curr = sorted[i]
    const rowBase = currentRow[0]
    const sameRow = curr.page === rowBase.page && Math.abs(curr.y - rowBase.y) <= threshold
    if (sameRow) {
      currentRow.push(curr)
    } else {
      rows.push(currentRow)
      currentRow = [curr]
    }
  }
  rows.push(currentRow)
  return rows
}

export async function parseFoireannPDF(file) {
  const arrayBuffer = await file.arrayBuffer()
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise

  const allItems = []
  for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
    const page = await pdf.getPage(pageNum)
    const textContent = await page.getTextContent()
    const viewport = page.getViewport({ scale: 1 })
    textContent.items.forEach(item => {
      const text = item.str.trim()
      if (!text) return
      allItems.push({
        text,
        x: item.transform[4],
        y: viewport.height - item.transform[5],
        page: pageNum,
      })
    })
  }

  const rows = groupIntoRows(allItems)

  // Calculate midX from page 1 only for player column splitting
  const page1Items = allItems.filter(i => i.page === 1)
  const page1X = page1Items.map(i => i.x)
  const midX = (Math.min(...page1X) + Math.max(...page1X)) / 2

  const result = { clubName: '', squadName: '', players: [], managers: [] }

  // Extract header info
  let foundImreoiri = false
  for (const row of rows) {
    const rowText = row.map(i => i.text).join(' ')
    if (rowText.includes('Imreoirí')) { foundImreoiri = true; break }
    const t = rowText.trim()
    if (!t || shouldSkip(t)) continue
    if (/^(U\d+|Senior|Minor|Junior|Intermediate)/i.test(t) && !result.squadName) {
      result.squadName = t
    } else if (!result.clubName && t.length > 2 && !/^\(/.test(t) && !/^\d/.test(t)) {
      result.clubName = t
    }
  }

  if (!foundImreoiri) throw new Error('Could not find player section (Imreoirí). Is this a Foireann team sheet?')

  // Process rows after Imreoirí
  let mode = 'players'
  let inContent = false
  const leftLines = []
  const rightLines = []
  const managerRows = []

  for (const row of rows) {
    const rowText = row.map(i => i.text).join(' ').trim()

    if (rowText.includes('Imreoirí')) { inContent = true; continue }
    if (!inContent) continue

    // Switch to managers mode — must check before shouldSkip
    if (rowText.includes('Bainisteo')) {
      mode = 'managers'
      continue
    }

    if (shouldSkip(rowText)) continue

    if (mode === 'players') {
      // Split into left/right columns using page 1 midX
      const left = row.filter(i => i.x < midX).map(i => i.text).join(' ').trim()
      const right = row.filter(i => i.x >= midX).map(i => i.text).join(' ').trim()
      if (left) leftLines.push(left)
      if (right) rightLines.push(right)
    } else {
      // Managers: treat each row as potentially containing left + right entries
      managerRows.push(row)
    }
  }

  // Parse player columns independently
  for (const columnLines of [leftLines, rightLines]) {
    let pendingName = null
    for (const line of columnLines) {
      if (!line || shouldSkip(line)) continue
      const m = line.match(PLAYER_DATA)
      if (m) {
        const [, dob, foireannId, englishName] = m
        result.players.push({
          irish_name: pendingName || englishName.trim(),
          english_name: englishName.trim(),
          dob: parseDOB(dob),
          foireann_id: foireannId,
        })
        pendingName = null
      } else {
        pendingName = line
      }
    }
  }

  // Parse managers — each row has Irish name row then data row, two columns wide
  // Process left and right sides of each manager row independently
  let leftPending = null
  let rightPending = null
  for (const row of managerRows) {
    const left = row.filter(i => i.x < midX).map(i => i.text).join(' ').trim()
    const right = row.filter(i => i.x >= midX).map(i => i.text).join(' ').trim()

    // Process left entry
    if (left) {
      const m = left.match(MANAGER_DATA)
      if (m) {
        const [, foireannId, englishName] = m
        result.managers.push({
          irish_name: leftPending || englishName.trim(),
          english_name: englishName.trim(),
          foireann_id: foireannId,
        })
        leftPending = null
      } else {
        leftPending = left
      }
    }

    // Process right entry
    if (right) {
      const m = right.match(MANAGER_DATA)
      if (m) {
        const [, foireannId, englishName] = m
        result.managers.push({
          irish_name: rightPending || englishName.trim(),
          english_name: englishName.trim(),
          foireann_id: foireannId,
        })
        rightPending = null
      } else {
        rightPending = right
      }
    }
  }

  return result
}